import { z } from 'zod';
import { AGENT_ROLES, AgentRoleSchema } from './states.js';
/**
 * Configuración del proveedor LLM (`01-producto/04-capa-agentica.md` §3.1).
 * - Proveedor **por defecto: Microsoft Foundry** (`LLM_PROVIDER=foundry`, cliente Foundry del SDK
 *   oficial `@anthropic-ai/foundry-sdk`). Anthropic API soportada por configuración.
 * - `openai`: cualquier endpoint compatible con Chat Completions (Azure OpenAI / Foundry Models con
 *   API key). El modelo lo decide el despliegue; los roles se mapean por tamaño (grande/medio/pequeño).
 * - Sin credenciales → `simulated` (respuestas grabadas) para pruebas y CI.
 * - Modelo y esfuerzo por rol, sobreescribibles en `tenant.settings.agents` y por variables de entorno.
 * El puerto que consume esta configuración es `LlmPort` (`ports/llm.ts`).
 */
export const LLM_PROVIDERS = ['foundry', 'anthropic', 'openai', 'simulated'];
export const LlmProviderSchema = z.enum(LLM_PROVIDERS).meta({ id: 'LlmProvider' });
export const DEFAULT_LLM_PROVIDER = 'foundry';
export const CLAUDE_MODELS = {
    OPUS: 'claude-opus-5-5',
    SONNET: 'claude-sonnet-5-5',
    HAIKU: 'claude-haiku-5-5',
};
export const LlmModelSchema = z
    .string()
    .regex(/^claude-[a-z0-9-]+$/)
    .meta({ id: 'LlmModel', examples: ['claude-opus-5-5'] });
/** `output_config.effort`. Fijarlo siempre explícito (default de Opus 5.5 = medium). */
export const LlmEffortSchema = z
    .enum(['low', 'medium', 'high', 'xhigh', 'max'])
    .meta({ id: 'LlmEffort' });
export const AgentModelConfigSchema = z.object({
    model: LlmModelSchema,
    effort: LlmEffortSchema,
    maxTokens: z.int().min(256).max(128_000),
});
/** Modelo/esfuerzo por defecto por rol. Cambiar un rol de modelo exige pasar sus evals. */
export const DEFAULT_AGENT_MODELS = {
    ORIGINATION_ADVISOR: { model: CLAUDE_MODELS.OPUS, effort: 'medium', maxTokens: 64_000 },
    DOCUMENT_READER: { model: CLAUDE_MODELS.OPUS, effort: 'high', maxTokens: 64_000 },
    CASH_FLOW_ANALYST: { model: CLAUDE_MODELS.OPUS, effort: 'high', maxTokens: 64_000 },
    PURCHASE_ASSISTANT: { model: CLAUDE_MODELS.SONNET, effort: 'low', maxTokens: 32_000 },
    ANALYST_COPILOT: { model: CLAUDE_MODELS.SONNET, effort: 'medium', maxTokens: 32_000 },
    DECISION_MEMO: { model: CLAUDE_MODELS.SONNET, effort: 'medium', maxTokens: 32_000 },
    EVAL_JUDGE: { model: CLAUDE_MODELS.SONNET, effort: 'medium', maxTokens: 16_000 },
    INPUT_GUARD: { model: CLAUDE_MODELS.HAIKU, effort: 'low', maxTokens: 1_024 },
    MEMORY_EXTRACTOR: { model: CLAUDE_MODELS.HAIKU, effort: 'low', maxTokens: 8_000 },
    SUMMARIZER: { model: CLAUDE_MODELS.HAIKU, effort: 'low', maxTokens: 8_000 },
};
/** Precios USD por millón de tokens (tarifa primera parte; Foundry factura igual). Verificar al desplegar. */
export const LlmPricingSchema = z.object({
    inputPerMTok: z.string(),
    outputPerMTok: z.string(),
    cacheReadPerMTok: z.string(),
    cacheWritePerMTok: z.string(),
});
export const DEFAULT_LLM_PRICING = {
    [CLAUDE_MODELS.OPUS]: {
        inputPerMTok: '4.00',
        outputPerMTok: '20.00',
        cacheReadPerMTok: '0.20',
        cacheWritePerMTok: '5.00',
    },
    [CLAUDE_MODELS.SONNET]: {
        inputPerMTok: '2.00',
        outputPerMTok: '10.00',
        cacheReadPerMTok: '0.20',
        cacheWritePerMTok: '2.50',
    },
    [CLAUDE_MODELS.HAIKU]: {
        inputPerMTok: '0.10',
        outputPerMTok: '0.50',
        cacheReadPerMTok: '0.01',
        cacheWritePerMTok: '0.125',
    },
};
/** Credenciales/endpoint por proveedor (los valores vienen de variables de entorno / Key Vault). */
export const LlmProviderConfigSchema = z
    .discriminatedUnion('provider', [
    z.object({
        provider: z.literal('foundry'),
        /** Nombre del recurso de Foundry (`ANTHROPIC_FOUNDRY_RESOURCE`) o URL base completa. */
        resource: z.string().min(1).optional(),
        baseUrl: z.url().optional(),
        /**
         * `key`: API key del recurso. `entra`: token de Microsoft Entra ID (identidad administrada /
         * `DefaultAzureCredential`), sin secretos en la configuración.
         */
        auth: z.enum(['key', 'entra']).default('key'),
        apiKey: z.string().min(1).optional(),
        /** Alcance del token de Entra (por defecto Cognitive Services). */
        tokenScope: z.string().min(1).optional(),
    }),
    z.object({
        provider: z.literal('anthropic'),
        apiKey: z.string().min(1),
        baseUrl: z.url().optional(),
    }),
    z.object({
        provider: z.literal('openai'),
        /** Endpoint del recurso, o URL completa de `/chat/completions`. */
        baseUrl: z.url(),
        apiKey: z.string().min(1),
        /** `api-version` para endpoints clásicos de Azure (`/openai/deployments/...`). */
        apiVersion: z.string().min(1).optional(),
        /** Despliegue por tamaño de modelo; `default` cubre los que falten. */
        models: z.object({
            default: z.string().min(1),
            large: z.string().min(1).optional(),
            medium: z.string().min(1).optional(),
            small: z.string().min(1).optional(),
        }),
        /** Tope de tokens de salida del despliegue (los roles piden hasta 64k). */
        maxOutputTokens: z.int().min(256).optional(),
        /**
         * `reasoning_effort` del despliegue. Algunos modelos de razonamiento (p. ej. GPT en Foundry)
         * solo aceptan herramientas en Chat Completions con `none`.
         */
        reasoningEffort: z.string().min(1).optional(),
        /** Precio USD por millón de tokens, para los presupuestos. */
        pricing: z
            .object({
            inputPerMTok: z.string(),
            outputPerMTok: z.string(),
            cachedInputPerMTok: z.string(),
        })
            .optional(),
    }),
    z.object({
        provider: z.literal('simulated'),
        /** Carpeta/escenario de respuestas grabadas. */
        fixturesPath: z.string().optional(),
    }),
])
    .refine((c) => c.provider !== 'foundry' || c.resource !== undefined || c.baseUrl !== undefined, {
    message: 'Foundry requiere `resource` o `baseUrl`',
})
    .refine((c) => c.provider !== 'foundry' || c.auth === 'entra' || c.apiKey !== undefined, {
    message: 'Foundry con auth=key requiere `apiKey`',
})
    .meta({ id: 'LlmProviderConfig' });
/**
 * Variables de entorno de la capa LLM (para el `src/config.ts` de `services/api`).
 * Si `LLM_PROVIDER` no tiene credenciales, el servicio debe degradar a `simulated`.
 */
/** `VAR=` vacío en `.env` cuenta como ausente. */
const blank = (schema) => z.preprocess((v) => (v === '' ? undefined : v), schema.optional());
export const LlmEnvSchema = z.object({
    LLM_PROVIDER: LlmProviderSchema.default(DEFAULT_LLM_PROVIDER),
    ANTHROPIC_FOUNDRY_API_KEY: blank(z.string()),
    ANTHROPIC_FOUNDRY_RESOURCE: blank(z.string()),
    ANTHROPIC_FOUNDRY_BASE_URL: blank(z.url()),
    /** `entra` = token de Entra ID (identidad administrada); por defecto `key` si hay API key. */
    ANTHROPIC_FOUNDRY_AUTH: blank(z.enum(['key', 'entra'])),
    ANTHROPIC_FOUNDRY_TOKEN_SCOPE: blank(z.string()),
    ANTHROPIC_API_KEY: blank(z.string()),
    /** `LLM_PROVIDER=openai`: endpoint compatible con Chat Completions + API key. */
    OPENAI_BASE_URL: blank(z.url()),
    OPENAI_API_KEY: blank(z.string()),
    OPENAI_API_VERSION: blank(z.string()),
    /** Despliegue por defecto y, opcional, por tamaño (roles Opus → large, Sonnet → medium, Haiku → small). */
    OPENAI_MODEL: blank(z.string()),
    OPENAI_MODEL_LARGE: blank(z.string()),
    OPENAI_MODEL_MEDIUM: blank(z.string()),
    OPENAI_MODEL_SMALL: blank(z.string()),
    OPENAI_MAX_OUTPUT_TOKENS: blank(z.coerce.number().int().min(256)),
    OPENAI_REASONING_EFFORT: blank(z.string()),
    /** Precios USD/MTok del despliegue (sin ellos, los presupuestos usan la tarifa de Opus: conservador). */
    OPENAI_PRICE_INPUT_PER_MTOK: blank(z.string()),
    OPENAI_PRICE_OUTPUT_PER_MTOK: blank(z.string()),
    OPENAI_PRICE_CACHED_INPUT_PER_MTOK: blank(z.string()),
    LLM_DAILY_BUDGET_USD: z.coerce.number().positive().default(10),
    LLM_USER_DAILY_BUDGET_USD: z.coerce.number().positive().default(2),
});
/** Sobrescritura de modelo por rol vía entorno: `LLM_MODEL_ORIGINATION_ADVISOR=claude-opus-5-5`. */
export function modelOverridesFromEnv(env) {
    const overrides = {};
    for (const role of AGENT_ROLES) {
        const value = env[`LLM_MODEL_${role}`];
        if (value && LlmModelSchema.safeParse(value).success)
            overrides[role] = value;
    }
    return overrides;
}
/** Resuelve la configuración del proveedor desde el entorno (o `simulated` si faltan credenciales). */
export function resolveLlmProviderConfig(env) {
    const foundryTarget = env.ANTHROPIC_FOUNDRY_RESOURCE ?? env.ANTHROPIC_FOUNDRY_BASE_URL;
    if (env.LLM_PROVIDER === 'foundry' && foundryTarget) {
        const target = {
            ...(env.ANTHROPIC_FOUNDRY_RESOURCE && { resource: env.ANTHROPIC_FOUNDRY_RESOURCE }),
            ...(env.ANTHROPIC_FOUNDRY_BASE_URL && { baseUrl: env.ANTHROPIC_FOUNDRY_BASE_URL }),
        };
        if (env.ANTHROPIC_FOUNDRY_AUTH === 'entra') {
            return {
                provider: 'foundry',
                auth: 'entra',
                ...target,
                ...(env.ANTHROPIC_FOUNDRY_TOKEN_SCOPE && { tokenScope: env.ANTHROPIC_FOUNDRY_TOKEN_SCOPE }),
            };
        }
        if (env.ANTHROPIC_FOUNDRY_API_KEY) {
            return { provider: 'foundry', auth: 'key', apiKey: env.ANTHROPIC_FOUNDRY_API_KEY, ...target };
        }
    }
    if (env.LLM_PROVIDER === 'anthropic' && env.ANTHROPIC_API_KEY) {
        return { provider: 'anthropic', apiKey: env.ANTHROPIC_API_KEY };
    }
    if (env.LLM_PROVIDER === 'openai' &&
        env.OPENAI_BASE_URL &&
        env.OPENAI_API_KEY &&
        env.OPENAI_MODEL) {
        const input = env.OPENAI_PRICE_INPUT_PER_MTOK;
        const output = env.OPENAI_PRICE_OUTPUT_PER_MTOK;
        return {
            provider: 'openai',
            baseUrl: env.OPENAI_BASE_URL,
            apiKey: env.OPENAI_API_KEY,
            ...(env.OPENAI_API_VERSION && { apiVersion: env.OPENAI_API_VERSION }),
            models: {
                default: env.OPENAI_MODEL,
                ...(env.OPENAI_MODEL_LARGE && { large: env.OPENAI_MODEL_LARGE }),
                ...(env.OPENAI_MODEL_MEDIUM && { medium: env.OPENAI_MODEL_MEDIUM }),
                ...(env.OPENAI_MODEL_SMALL && { small: env.OPENAI_MODEL_SMALL }),
            },
            ...(env.OPENAI_MAX_OUTPUT_TOKENS !== undefined && {
                maxOutputTokens: env.OPENAI_MAX_OUTPUT_TOKENS,
            }),
            ...(env.OPENAI_REASONING_EFFORT && { reasoningEffort: env.OPENAI_REASONING_EFFORT }),
            ...(input &&
                output && {
                pricing: {
                    inputPerMTok: input,
                    outputPerMTok: output,
                    cachedInputPerMTok: env.OPENAI_PRICE_CACHED_INPUT_PER_MTOK ?? input,
                },
            }),
        };
    }
    return { provider: 'simulated' };
}
/** Presupuestos de tokens/USD con acción al superar. */
export const BudgetActionSchema = z.enum(['DEGRADE', 'PAUSE', 'NOTIFY']);
export const BudgetScopeSchema = z.enum(['TURN', 'SESSION', 'CUSTOMER_DAY', 'TENANT_DAY']);
export const AgentBudgetSchema = z.object({
    scope: BudgetScopeSchema,
    limitTokens: z.int().min(1).nullable(),
    limitUsd: z.string().nullable(),
    action: BudgetActionSchema,
    /** Fracción para alertar (0.8 = 80 %). */
    alertAt: z.string().default('0.80'),
});
/** `tenant.settings.agents` / `/admin/settings/ai`. */
export const AgentSettingsSchema = z
    .object({
    models: z.partialRecord(AgentRoleSchema, AgentModelConfigSchema).default({}),
    budgets: z.array(AgentBudgetSchema).default([]),
    /** Kill switch por agente conversacional. */
    enabled: z.object({
        ORIGINATION_ADVISOR: z.boolean(),
        PURCHASE_ASSISTANT: z.boolean(),
        ANALYST_COPILOT: z.boolean(),
    }),
    limits: z.object({
        maxToolIterationsPerTurn: z.int().min(1).max(32).default(8),
        maxTurnsPerSession: z.int().min(1).default(200),
    }),
    voice: z.object({ enabled: z.boolean(), voice: z.string() }),
})
    .meta({ id: 'AgentSettings' });
export const DEMO_AGENT_SETTINGS = {
    models: {},
    budgets: [
        { scope: 'TURN', limitTokens: 200_000, limitUsd: null, action: 'PAUSE', alertAt: '0.80' },
        {
            scope: 'CUSTOMER_DAY',
            limitTokens: null,
            limitUsd: '2.00',
            action: 'PAUSE',
            alertAt: '0.80',
        },
        { scope: 'TENANT_DAY', limitTokens: null, limitUsd: '10.00', action: 'PAUSE', alertAt: '0.80' },
    ],
    enabled: { ORIGINATION_ADVISOR: true, PURCHASE_ASSISTANT: true, ANALYST_COPILOT: true },
    limits: { maxToolIterationsPerTurn: 8, maxTurnsPerSession: 200 },
    voice: { enabled: true, voice: 'es-MX-JorgeMultilingualNeural' },
};
export function resolveAgentModel(role, settings) {
    return settings?.models[role] ?? DEFAULT_AGENT_MODELS[role];
}
//# sourceMappingURL=llm.js.map